import { ReactNode } from 'react';

import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import { Text } from 'components/Text/Text';
import { useViewportBreakpoint } from 'hooks/viewport/useViewportBreakpoint';

import {
  LinkContainer,
  Separator,
  SingleRouterLink,
  StyledRouterLink,
} from './Breadcrumbs.styled';

export type BreadcrumbLink = {
  'data-qa-id'?: string;
  title: ReactNode;
  to?: string;
  state?: Record<string, unknown>;
};

type Props = {
  'data-qa-id'?: string;
  links: Array<BreadcrumbLink>;
};

export function Breadcrumbs({ links, ...props }: Props) {
  const breakpoint = useViewportBreakpoint();
  const isMobile = breakpoint === 'xs';
  const shouldDisplayBackLinkOnly = isMobile && links.length > 1;

  const renderSingleBackLink = () => {
    const previousLink = links.at(-2);
    if (!previousLink) return null;
    if (!previousLink.to) return null;

    return (
      <LinkContainer>
        <SingleRouterLink to={previousLink.to}>
          <>
            <Icon name="chevron-left" size={11} />
            Back
          </>
        </SingleRouterLink>
      </LinkContainer>
    );
  };

  const renderFullBreadcrumbs = () =>
    links.map(({ title, to, state, ...linkProps }, index) => (
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/no-array-index-key
      <LinkContainer key={index}>
        {index > 0 && (
          <Separator>
            <Icon name="chevron-left" size={11} />
          </Separator>
        )}
        <Box
          component={to ? StyledRouterLink : undefined}
          // @ts-expect-error replace Box with the component
          to={to || undefined}
          state={state}
          whiteSpace="nowrap"
          overflow="hidden"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...linkProps}
        >
          {title}
        </Box>
      </LinkContainer>
    ));

  return (
    <Box
      component={Text}
      styleAs="bodySmall"
      display="flex"
      flexWrap="nowrap"
      alignItems="center"
      lineHeight={1.5}
      data-qa-id="breadcrumbs"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {shouldDisplayBackLinkOnly
        ? renderSingleBackLink()
        : renderFullBreadcrumbs()}
    </Box>
  );
}
